import { FC, useCallback, useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import {
    Dialog,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogActions,
    DialogContent,
    Button,
    makeStyles,
    mergeClasses,
    shorthands,
} from '@fluentui/react-components';
import { useMsal } from '@azure/msal-react';
import { logoutAsync } from '../../../services/auth/AuthService';
import { customColors, customFonts } from '../../../styles';

// This file should provide the temp-native idle timer alert/logout behavior.
//
// Primary responsibility:
// - prompt the user before idle logout
// - log the user out when they remain idle
// - allow the user to continue their session before idle timeout expires
//
// Notes:
// - SignalR reconnection logic is intentionally removed in the temp app
// - future session-refresh behavior can be added here if the new auth flow requires it

const useClasses = makeStyles({
    root: {
        width: '450px',
        borderRadius: '15px',
    },
    continue: {
        backgroundColor: customColors.protivitiOrange,
        '&:hover': {
            backgroundColor: customColors.summarizeButtonDarker,
            color: customColors.white,
        },
    },
    logout: {
        backgroundColor: customColors.white,
        '&:hover': {
            backgroundColor: customColors.summarizeButtonDarker,
            color: customColors.white,
        },
    },
    button: {
        fontFamily: customFonts.Lato,
        fontSize: '12px',
        borderRadius: '5px',
        ...shorthands.border('1.5px', 'solid', customColors.protivitiOrange),
    },
    title: {
        fontFamily: customFonts.TitleFont,
        fontWeight: '500',
        fontSize: '24px',
    },
    content: {
        fontFamily: customFonts.Lato,
    },
});

export interface IdleTimerAlertProps {
    enabled?: boolean;
}

export interface IdleTimerAlertProps {
    enabled?: boolean;
}

export const IdleTimerAlert: FC<IdleTimerAlertProps> = ({ enabled = true }) => {
    const classes = useClasses();
    const { instance } = useMsal();
    const timeout = Number(process.env.REACT_APP_IdleTimeOut ?? 3300000);
    const promptBeforeIdle = 120000;

    const [remaining, setRemaining] = useState<number>(Math.ceil(timeout / 1000));
    const [open, setOpen] = useState<boolean>(false);

    const onLogout = useCallback(async () => {
        setOpen(false);
        await logoutAsync({ msalInstance: instance });
    }, [instance]);

    const onIdle = () => {
        setOpen(false);
        void onLogout();
    };

    const onActive = () => {
        setOpen(false);
    };

    const onPrompt = () => {
        setOpen(true);
    };

    const { getRemainingTime, reset } = useIdleTimer({
        onPrompt,
        onIdle,
        onActive,
        timeout,
        promptBeforeIdle,
        throttle: 500,
        disabled: !enabled,
    });

    useEffect(() => {
        if (!enabled) {
            return;
        }

        setRemaining(Math.ceil(getRemainingTime() / 1000));
        const interval = setInterval(() => {
            setRemaining(Math.ceil(getRemainingTime() / 1000));
        }, 500);

        return () => {
            clearInterval(interval);
        };
    }, [enabled, getRemainingTime]);

    const formatTime = (seconds: number) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes < 10 ? '0' : ''}${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    const onContinue = () => {
        setOpen(false);
        reset();
    };

    if (!enabled) {
        return null;
    }

    return (
        <Dialog open={open}>
            <DialogSurface className={classes.root}>
                <DialogBody>
                    <DialogTitle className={classes.title}>Alert</DialogTitle>
                    <DialogContent className={classes.content}>Logging out in {formatTime(remaining)}</DialogContent>
                    <DialogActions>
                        <Button
                            style={{ transition: 'all 0.3s ease' }}
                            className={mergeClasses(classes.logout, classes.button)}
                            appearance="secondary"
                            onClick={() => {
                                void onLogout();
                            }}
                        >
                            Logout
                        </Button>
                        <Button
                            style={{ transition: 'all 0.3s ease' }}
                            className={mergeClasses(classes.continue, classes.button)}
                            onClick={onContinue}
                        >
                            Continue
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default IdleTimerAlert;
